import React from "react";

const RoomContext = React.createContext(undefined);
const PROFILE_TTL_MS = 24 * 60 * 60 * 1000;

const storageKey = (buildingCode) =>
  `hebe_hgs_profile_${buildingCode || "default"}`;

const readStoredProfile = (buildingCode) => {
  if (typeof window === "undefined") return null;

  try {
    const raw = localStorage.getItem(storageKey(buildingCode));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const expiresAt = Number(parsed?.expiresAt || 0);
    if (!expiresAt || Date.now() > expiresAt) {
      localStorage.removeItem(storageKey(buildingCode));
      return null;
    }

    return {
      guestName: parsed.guestName || null,
      roomNumber: parsed.roomNumber || null,
    };
  } catch (_e) {
    return null;
  }
};

const writeStoredProfile = ({ buildingCode, guestName, roomNumber }) => {
  if (typeof window === "undefined") return;

  const payload = {
    guestName: guestName || "",
    roomNumber: roomNumber || "",
    expiresAt: Date.now() + PROFILE_TTL_MS,
  };
  localStorage.setItem(storageKey(buildingCode), JSON.stringify(payload));
};

export const RoomProvider = ({ children }) => {
  const [roomNumber, setRoomNumber] = React.useState(null);
  const [guestName, setGuestName] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const loadCachedProfile = React.useCallback((buildingCode) => {
    const cached = readStoredProfile(buildingCode);
    if (!cached) return false;

    setGuestName(cached.guestName);
    setRoomNumber(cached.roomNumber);
    setError(null);
    return true;
  }, []);

  const setGuestProfile = async ({ name, room, buildingCode }) => {
    setIsLoading(true);
    setError(null);
    const nameValue = name?.trim();
    const roomValue = room?.trim();

    if (!nameValue && !roomValue) {
      setError("Please fill room number or your name.");
      setIsLoading(false);
      return false;
    }

    if (roomValue) {
      try {
        const response = await fetch(
          `/api/v1/room_guest?building_code=${encodeURIComponent(buildingCode || "")}&room_no=${encodeURIComponent(roomValue)}`,
          { headers: { Accept: "application/json" } },
        );
        const payload = await response.json();
        if (response.ok) {
          const nextRoomNumber =
            payload?.data?.room_number || roomValue || null;
          const nextGuestName = nameValue || payload?.data?.guest_name || "";
          setRoomNumber(nextRoomNumber);
          setGuestName(nextGuestName);
          writeStoredProfile({
            buildingCode,
            guestName: nextGuestName,
            roomNumber: nextRoomNumber,
          });
          setIsLoading(false);
          return true;
        }
      } catch (_error) {
        // Fall back to manual name flow.
      }
    }

    if (!nameValue) {
      setError("Room not found as checked-in guest. Please fill your name.");
      setIsLoading(false);
      return false;
    }

    setGuestName(nameValue);
    setRoomNumber(roomValue || null);
    writeStoredProfile({
      buildingCode,
      guestName: nameValue,
      roomNumber: roomValue || null,
    });
    setIsLoading(false);
    return true;
  };

  // Override the locally-stored profile with the authoritative booking data once the guest has
  // successfully logged in (name = booking's guest, room = first line's room). Informative only —
  // no backend call, just refresh state + localStorage so headers show the real room, not the
  // possibly-wrong number typed on first visit.
  const applyBookingProfile = React.useCallback(
    ({ name, roomNumber: room, buildingCode }) => {
      const nameValue = (name || "").trim();
      const roomValue = (room || "").trim();
      if (!nameValue && !roomValue) return false;

      setGuestName(nameValue || null);
      setRoomNumber(roomValue || null);
      setError(null);
      writeStoredProfile({
        buildingCode,
        guestName: nameValue,
        roomNumber: roomValue,
      });
      return true;
    },
    [],
  );

  const clearRoom = (buildingCode = null) => {
    setRoomNumber(null);
    setGuestName(null);
    setError(null);
    if (typeof window !== "undefined" && buildingCode) {
      localStorage.removeItem(storageKey(buildingCode));
    }
  };

  const contextValue = React.useMemo(
    () => ({
      roomNumber,
      guestName,
      isLoading,
      error,
      setGuestProfile,
      applyBookingProfile,
      clearRoom,
      loadCachedProfile,
    }),
    [
      roomNumber,
      guestName,
      isLoading,
      error,
      setGuestProfile,
      applyBookingProfile,
      clearRoom,
      loadCachedProfile,
    ],
  );

  return (
    <RoomContext.Provider value={contextValue}>{children}</RoomContext.Provider>
  );
};

export const useRoom = () => {
  const context = React.use(RoomContext);
  if (context === undefined) {
    throw new Error("useRoom must be used within a RoomProvider");
  }
  return context;
};
